import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { useDeviceSearch } from '../hooks/useDeviceSearch';
import DecommissionedBadge from '../components/DecommissionedBadge';
import Button from '../components/ui/Button';
import ActionRow from '../components/ui/ActionRow';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';
import { Checkbox } from '../components/pilot-ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/pilot-ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/pilot-ui/tooltip';
import { DeviceSearchRow } from '../types';
import { CLIP_LIFECYCLE_DOC_URL, CLIP_LIFECYCLE_DOC_TITLE } from '../utils/docLinks';
import {
  APP_RECORD_DELETE_EFFECTS,
  RETURN_TO_INVENTORY_CLEARS,
  RETURN_TO_INVENTORY_KEEPS,
  RETURN_TO_INVENTORY_LEAD,
} from '../utils/lifecycleWording';

// Plan 91 Phase 6 — Data Cleanup Console.
// Drives the device-data-api cleanup API: preview (counts + plan), typed-serial
// confirmed execute (enqueues a job), and live job/step polling. All five
// templates the backend executes are exposed: three RDS templates, the
// app-side Firestore cleanup and Return to inventory.

type TemplateKey = 'reprocess_only' | 'reset_device_as_of_date' | 'delete_recent_corrupted_data' | 'firestore_cleanup' | 'return_to_inventory';
type WindowMode = 'all' | 'before' | 'after' | 'between';

const TEMPLATES: { key: TemplateKey; label: string; blurb: string; modes: WindowMode[] }[] = [
  { key: 'reprocess_only', label: 'Reprocess only', blurb: 'No raw deletion — clear derived analytics + re-run. Readings preserved.', modes: ['all', 'after', 'between'] },
  { key: 'reset_device_as_of_date', label: 'Reset device as of date', blurb: 'Delete readings before a cutoff + ALL derived; reset baseline; rebuild from cutoff.', modes: ['before'] },
  { key: 'delete_recent_corrupted_data', label: 'Delete recent corrupted data', blurb: 'Delete readings + derived inside a window. Baseline preserved by default.', modes: ['after', 'between'] },
  { key: 'firestore_cleanup', label: 'Firestore cleanup (app-side)', blurb: `${APP_RECORD_DELETE_EFFECTS.join(' ')} Also removes HVAC and location records left empty. Requires delete_devices.`, modes: ['all'] },
  { key: 'return_to_inventory', label: 'Return to inventory', blurb: `${RETURN_TO_INVENTORY_LEAD} For QC’d Clips being reshipped: deletes all of the Clip’s readings and derived data, its app records, device logs, read bundles, heat/cool model results and pressure-state evidence. Operator notes are kept unless “Clear notes” is checked. The Clip’s IoT identity is kept, so the next owner can set it up right away. It moves to the default path for new devices when that is a configured option; otherwise its path is kept. Requires delete_devices and full device scope (/).`, modes: ['all'] },
];

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

// FS-909: Return to inventory moves the Clip to the default path for new
// devices, which a scoped account's grant need not cover, so the API refuses it
// to anything less than an exact `/` grant. Shown on the controls it disables.
const FULL_SCOPE_REASON = 'Return to inventory requires full device scope (/).';

// Plain-language description of each preview row, shown in the hover "i" tooltip.
const COMPONENT_DESCRIPTIONS: Record<string, string> = {
  rds_readings: 'Raw sensor measurements (temperature, pressure, humidity) the device reported. The bulk of stored data.',
  rds_daily_calcs: 'One row per day of computed metrics (filter centroids, baselines) derived from the raw readings.',
  rds_runtimes: 'HVAC on/off run-time segments inferred from the readings.',
  rds_filter_metrics: 'Per-filter pressure-drop and remaining-life metrics computed for this device.',
  rds_alerts: 'Alerts raised for this device (filter due, no-data, etc.).',
  rds_device_info_log: 'History of firmware / config / status reports the device sent.',
  rds_notes: 'Operator notes attached to this device (QC findings, hardware observations, repairs). Every template keeps them unless return-to-inventory is run with “Clear notes” checked.',
  rds_device_analysis_models: 'Saved analysis model parameters for this device.',
  rds_device_analysis_events: 'Computed analysis event history for this device.',
  rds_device_analysis_assignments: 'Which analyses are enabled for this device (incl. the default). All are deleted; the configured default analysis (as flagged in the analysis definitions) is recreated.',
  rds_device_analysis_side_effects: 'Side-effect records tied to this device’s analysis assignments (cascade-deleted with them).',
  rds_device_app_context: 'Cached app-context (ownership/claims) projection for this device.',
  firestore_device_docs: "The device's record(s) in the mobile-app database (Firestore), across all owners.",
  firestore_hvac_branches: 'HVAC-system records in the app that point only at this device (emptied branches are removed).',
  firestore_location_branches: 'Location / home records in the app that point only at this device (emptied branches are removed).',
  s3_read_bundles: "Raw IoT read bundles in S3 — the original device payloads readings were decoded from. Per-device, so only this serial's objects are deleted (return-to-inventory only).",
  s3_cycle_model_results: "Heat/cool model results in S3 — the experimental cycle model's saved day results for this Clip, under its device ID. Every model version is deleted (return-to-inventory only).",
  s3_pressure_state_evidence: "Pressure-state evidence in S3 — the pressure-state analysis's saved day summaries for this Clip (runs, level changes, 5-minute blocks), under its device ID. Every version is deleted (return-to-inventory only).",
};

const InfoIcon: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger aria-label={text} title={text} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={() => setOpen(true)}
          className="ml-1 inline-flex size-4 cursor-help items-center justify-center rounded-full border border-control-border text-xs font-semibold text-muted-foreground align-middle">
          i
        </TooltipTrigger>
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// FS-730: reprocess_only rebuilds the device's own analyses (no picker); the
// preview says which, which one owns the baseline and what happens to it.
// Absent when the API predates FS-730.
interface ReprocessAnalysis {
  analysis_key: string;
  kind: 'tracker' | 'window';
  roles: Array<'production' | 'baseline_owner'>;
  synthesized?: boolean;
  delete: { mode: 'window' } | { mode: 'from'; from: string | null };
  clear_model: boolean;
  daily_calcs_to_delete: number;
  models_to_clear: number;
  note: string;
}
interface ReprocessSummary {
  source: 'device_analyses' | 'new_device_default' | 'explicit_analysis_key';
  analyses: ReprocessAnalysis[];
  skipped: { analysis_key: string; reason: string }[];
  production_analysis: string | null;
  baseline_owner: string | null;
  baseline: { action: 'full_reset' | 'cache_only' | 'untouched'; owner: string | null; clears: string[]; note: string };
  notes: string[];
}

const BASELINE_ACTION_LABEL: Record<ReprocessSummary['baseline']['action'], string> = {
  full_reset: 'reset — re-measured by the rebuild',
  cache_only: 'kept — only the airflow percentage is cleared',
  untouched: 'untouched',
};

const ReprocessSummaryBox: React.FC<{ summary: ReprocessSummary }> = ({ summary }) => (
  <div data-testid="reprocess-summary" className="mb-3 rounded-sm border border-status-info-border bg-blue-50 p-3 text-xs text-gray-700">
    <p className="font-semibold text-blue-800 mb-2">Analyses to reprocess — the device's own, not chosen here</p>
    <ul className="space-y-1 mb-2">
      {summary.analyses.map((a) => {
        const span = a.delete.mode === 'window'
          ? 'rows inside the window'
          : a.delete.from ? `rows from ${a.delete.from} through the latest day` : 'every row';
        return (
          <li key={a.analysis_key} title={a.note}>
            <span className="font-mono font-semibold">{a.analysis_key}</span>
            {a.roles.includes('production') && (
              <span className="ml-1 rounded-sm border border-status-success-border bg-status-success px-1.5 py-0.5 text-[10px] uppercase text-status-success-foreground" title="Owns the daily filter health numbers the app shows">production</span>
            )}
            {a.roles.includes('baseline_owner') && (
              <span className="ml-1 rounded-sm border border-status-warning-border bg-status-warning px-1.5 py-0.5 text-[10px] uppercase text-status-warning-foreground" title="Owns filter_baseline_mutation: Keep / Reset baseline applies to this analysis">owns the baseline</span>
            )}
            {a.synthesized && (
              <span className="ml-1 rounded-sm border border-status-neutral-border bg-status-neutral px-1.5 py-0.5 text-[10px] uppercase text-status-neutral-foreground" title="The device has no analysis assignments; this is the new-device default it runs">default</span>
            )}
            <span className="ml-2 text-gray-600">
              {a.kind === 'tracker' ? `model rebuilt (${a.models_to_clear} cleared); ` : ''}
              {a.daily_calcs_to_delete} daily rows deleted and rebuilt ({span})
            </span>
          </li>
        );
      })}
    </ul>
    {summary.skipped.length > 0 && (
      <p className="mb-2 text-gray-600">Not reprocessed: {summary.skipped.map((s) => `${s.analysis_key} (${s.reason})`).join('; ')}</p>
    )}
    <p title={summary.baseline.clears.length ? `Cleared on the device: ${summary.baseline.clears.join(', ')}` : 'No device baseline column is changed'}>
      <span className="font-semibold">Baseline {BASELINE_ACTION_LABEL[summary.baseline.action]}.</span> {summary.baseline.note}
    </p>
    {summary.notes.map((n) => (
      <p key={n} className="mt-1 text-gray-600">{n}</p>
    ))}
  </div>
);

interface LifecycleEffect {
  field: string;
  label: string;
  effect: 'kept' | 'cleared' | 'may_change';
  note: string;
}

const EFFECT_LABEL: Record<LifecycleEffect['effect'], { text: string; cls: string }> = {
  kept: { text: 'Kept', cls: 'text-gray-600' },
  cleared: { text: 'Cleared', cls: 'text-status-error-foreground font-semibold' },
  may_change: { text: 'May change', cls: 'text-amber-700 font-semibold' },
};

const LifecycleTable: React.FC<{ rows: LifecycleEffect[] }> = ({ rows }) => (
  <div data-testid="lifecycle-effects" className="mb-3">
    <p className="text-xs font-semibold text-gray-700 mb-1">What this does to the Clip’s lifecycle</p>
    <table className="w-full text-xs">
      <thead><tr className="text-left text-muted-foreground"><th className="py-1">Field</th><th>Effect</th><th>Note</th></tr></thead>
      <tbody>
        {rows.map((r) => {
          const e = EFFECT_LABEL[r.effect] ?? { text: r.effect, cls: '' };
          return (
            <tr key={r.field} className="border-t align-top">
              <td className="py-1 pr-2" title={r.field}>{r.label}</td>
              <td className={`py-1 pr-2 whitespace-nowrap ${e.cls}`}>{e.text}</td>
              <td className="py-1 text-gray-600">{r.note}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const TableList: React.FC<{ testId: string; title: string; tables: string[]; tone: 'red' | 'gray' }> = ({ testId, title, tables, tone }) => (
  <div data-testid={testId} className={`mb-3 rounded-sm border p-3 ${tone === 'red' ? 'border-status-error-border bg-red-50' : 'border-border bg-gray-50'}`}>
    <p className={`text-xs font-semibold mb-2 ${tone === 'red' ? 'text-red-700' : 'text-gray-600'}`}>{title}</p>
    {tables.length === 0 ? (
      <p className="text-xs text-foreground">None.</p>
    ) : (
      <ul className="flex flex-wrap gap-x-3 gap-y-1">
        {tables.map((t) => (
          <li key={t} className={`text-xs font-mono ${tone === 'red' ? 'text-red-700' : 'text-gray-700'}`}>{t}</li>
        ))}
      </ul>
    )}
  </div>
);

interface PreviewResponse {
  preview_id: string;
  target: { device_id: number; serial: string };
  template: TemplateKey;
  counts: Record<string, number>;
  destructive_components: string[];
  retained?: { component: string; note: string }[];
  resolved_plan: Record<string, unknown>;
  reprocess?: ReprocessSummary;
  // What the template does to each lifecycle field, and every table it
  // deletes or keeps. Absent from an older API; the console then falls back to
  // the uncounted-cascade box.
  lifecycle?: LifecycleEffect[];
  deletes?: string[];
  keeps?: string[];
  expires_at: string;
  confirmation_required: { typed_device_serial: string };
}
interface DataRange { has_data: boolean; min_data: string | null; max_data: string | null }
interface JobStep { step_key: string; state: string; counts: Record<string, unknown> }
interface JobResponse {
  id: string; state: string; template: string;
  execution_counts: Record<string, unknown>; error: string | null;
  completed_at: string | null; steps: JobStep[];
}

const errMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

// datetime-local (browser local time) → UTC ISO 8601 with Z, as the API expects.
const toIso = (local: string): string | null => {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

// UTC ISO (or Date) → datetime-local string in BROWSER LOCAL time (YYYY-MM-DDTHH:mm),
// for the date pickers' value / min / max attributes.
const toLocalInput = (v: string | Date | null): string => {
  if (!v) return '';
  const d = typeof v === 'string' ? new Date(v) : v;
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DataCleanupConsolePage: React.FC = () => {
  const { hasPermission, scopes } = useAuth();
  const canPurge = hasPermission('purge_device_data');
  const fullScope = Array.isArray(scopes) && scopes.includes('/');
  const scopeRefusal = (t: TemplateKey): string | null =>
    (t === 'return_to_inventory' && !fullScope ? FULL_SCOPE_REASON : null);
  // firestore_cleanup additionally requires delete_devices server-side; mirror that
  // here so a purge-only user isn't offered a Run that 403s.
  const TEMPLATE_EXTRA_PERM: Record<string, string> = { firestore_cleanup: 'delete_devices', return_to_inventory: 'delete_devices' };

  // Target device.
  const [query, setQuery] = useState('');
  const [device, setDevice] = useState<DeviceSearchRow | null>(null);
  const search = useDeviceSearch({ q: query, page: 1, pageSize: 8 });

  // Plan inputs.
  const [template, setTemplate] = useState<TemplateKey>('reprocess_only');
  const tpl = TEMPLATES.find((t) => t.key === template)!;
  const [mode, setMode] = useState<WindowMode>('all');
  const [cutoff, setCutoff] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [keepBaseline, setKeepBaseline] = useState(true);
  const [resetBaseline, setResetBaseline] = useState(false);
  const [rebuild, setRebuild] = useState(true);
  // FS-500: return_to_inventory keeps operator notes by default; checking this wipes them.
  const [clearNotes, setClearNotes] = useState(false);

  // Device data range — drives the date-picker bounds (no future, no pre-data).
  const [range, setRange] = useState<DataRange | null>(null);

  // Results.
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [confirm, setConfirm] = useState('');
  const [job, setJob] = useState<JobResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the window mode valid for the chosen template; clear another template's job.
  useEffect(() => {
    if (!tpl.modes.includes(mode)) setMode(tpl.modes[0]);
    setJob(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // FS-912: a preview is for the exact inputs it was made with, and Run executes
  // the stored preview, not what the form shows now. Any change to the device,
  // template, window or an option retires it and the typed serial, so Run waits
  // for a new preview; previewGen tells a reply still in flight that its inputs
  // are gone.
  const previewGen = useRef(0);
  useEffect(() => {
    previewGen.current += 1;
    setPreview(null); setConfirm('');
  }, [device?.id, template, mode, cutoff, start, end, keepBaseline, resetBaseline, rebuild, clearNotes]);

  const buildTimeWindow = (): Record<string, string> | null => {
    if (mode === 'all') return { mode };
    if (mode === 'before') { const c = toIso(cutoff); return c ? { mode, cutoff: c } : null; }
    if (mode === 'after') { const c = toIso(cutoff); return c ? { mode, cutoff: c } : null; }
    const s = toIso(start); const e = toIso(end);
    return s && e ? { mode, start: s, end: e } : null;
  };

  const buildOptions = (): Record<string, unknown> => {
    // FS-730: no analysis key — the API reprocesses the device's own analyses
    // and applies the baseline choice to whichever one owns the baseline.
    if (template === 'reprocess_only') return { keep_baseline: keepBaseline };
    if (template === 'delete_recent_corrupted_data') return { reset_baseline: resetBaseline, rebuild };
    if (template === 'return_to_inventory') return { clear_notes: clearNotes };
    return {}; // reset_device_as_of_date: no options
  };

  const base = config.devicesApiUrl;

  // Fetch the device's data range when a device is selected, to bound the pickers.
  useEffect(() => {
    if (!device) { setRange(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get<DataRange>(`${base}/data-cleanups/device-data-range/${device.id}`);
        if (!cancelled) setRange(res.data);
      } catch { if (!cancelled) setRange(null); }
    })();
    return () => { cancelled = true; };
  }, [device, base]);

  // Picker bounds: never the future; never before the device's first stored data.
  const maxLocal = toLocalInput(new Date());
  const minLocal = range?.min_data ? toLocalInput(range.min_data) : undefined;

  // Guard against typed-in dates that bypass the picker min/max.
  const validateDates = (): string | null => {
    const now = Date.now();
    const floor = range?.min_data ? new Date(range.min_data).getTime() : -Infinity;
    const check = (label: string, v: string): string | null => {
      if (!v) return null;
      const t = new Date(v).getTime();
      if (t > now) return `${label} can't be in the future.`;
      if (t < floor) return `${label} is before the device's first data (${new Date(range!.min_data!).toLocaleString()}).`;
      return null;
    };
    return check('Cutoff', cutoff) || check('Start', start) || check('End', end);
  };

  const runPreview = async () => {
    if (!device) return;
    const time_window = buildTimeWindow();
    if (!time_window) { setError('Fill in the date(s) for the selected window mode.'); return; }
    const dateErr = validateDates();
    if (dateErr) { setError(dateErr); return; }
    const gen = ++previewGen.current;
    setBusy(true); setError(null); setJob(null); setConfirm('');
    try {
      const res = await axiosInstance.post<PreviewResponse>(`${base}/data-cleanups/preview`, {
        target: { type: 'device', device_id: device.id },
        template, time_window, options: buildOptions(),
      });
      if (gen === previewGen.current) setPreview(res.data);
    } catch (err) {
      if (gen === previewGen.current) {
        setPreview(null);
        setError(errMsg(err, 'Preview failed.'));
      }
    } finally { setBusy(false); }
  };

  const runExecute = async () => {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const res = await axiosInstance.post<JobResponse>(`${base}/data-cleanups`, {
        preview_id: preview.preview_id,
        idempotency_key: crypto.randomUUID(),
        confirmation: { typed_device_serial: confirm },
      });
      setJob(res.data);
      setPreview(null); setConfirm('');
    } catch (err) {
      setError(errMsg(err, 'Execute failed.'));
    } finally { setBusy(false); }
  };

  // Poll the job while it's queued/running.
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!job || TERMINAL.has(job.state)) return;
    pollRef.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get<JobResponse>(`${base}/data-cleanups/${job.id}`);
        setJob(res.data);
      } catch { /* keep last state; next render reschedules */ }
    }, 2000);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [job, base]);

  const expired = preview ? new Date(preview.expires_at).getTime() < Date.now() : false;
  const confirmOk = !!preview && !expired && confirm === preview.confirmation_required.typed_device_serial;

  const needsCutoff = mode === 'before' || mode === 'after';
  const needsRange = mode === 'between';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-1">Data Cleanup Console</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Preview and run device data cleanup. Destructive — readings deletes are permanent.{' '}
        <a href={CLIP_LIFECYCLE_DOC_URL} target="_blank" rel="noopener noreferrer"
          title={CLIP_LIFECYCLE_DOC_TITLE} className="text-link hover:underline">
          What each action does to a Clip&apos;s lifecycle
        </a>
      </p>

      {/* API refusals (e.g. a legacy datalogger with no IoT identity can't be
          returned to inventory) arrive here verbatim. */}
      {error && <div role="alert" className="mb-4 p-3 rounded-sm bg-red-50 border border-status-error-border text-red-700 text-sm">{error}</div>}

      {/* 1. Target device */}
      <section className="mb-6 p-4 bg-white rounded-lg border">
        <h2 className="font-semibold text-gray-700 mb-2">1. Target device</h2>
        {device ? (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{device.name} <span className="text-muted-foreground">(id {device.id})</span></span>
                <DecommissionedBadge decommissioned={device.decommissioned} />
                {device.app_context.status === 'ambiguous' && (
                  <span className="rounded-sm border border-status-warning-border bg-status-warning px-1.5 py-0.5 text-[10px] font-semibold uppercase text-status-warning-foreground" title="More than one current app owner">multi-owner</span>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                {device.description && <span className="font-medium text-gray-600">“{device.description}”</span>}
                <span>{device.entity_path || '—'}</span>
                {device.app_context.status !== 'missing'
                  ? <span>· owner: {device.app_context.owner_email || device.app_context.owner_name || 'Unknown owner'}{device.app_context.location_name ? ` · ${device.app_context.location_name}` : ''}{device.app_context.hvac_name ? ` / ${device.app_context.hvac_name}` : ''}</span>
                  : <span className="text-muted-foreground">· no app registration</span>}
                {device.app_context.filter_summary && <span>· filter: {device.app_context.filter_summary}</span>}
              </div>
            </div>
            <Button variant="link" size="md" className="shrink-0" onClick={() => { setDevice(null); setPreview(null); setJob(null); }}
              title="Pick a different device; the preview and any job shown here are cleared">change</Button>
          </div>
        ) : (
          <>
            <Label htmlFor="cleanup-device-search">Search for a device</Label>
            <p id="cleanup-search-help" className="mb-1 text-xs text-muted-foreground">Find the serial to preview cleanup. Search by serial, ID, description, owner email or location.</p>
            <Input id="cleanup-device-search" className="mb-2" placeholder="Search serial, id, description, owner, email, location…"
              value={query} onChange={(e) => setQuery(e.target.value)} aria-describedby="cleanup-search-help" />
            {search.loading && <p className="text-sm text-muted-foreground">Searching…</p>}
            {search.error && <div role="alert" className="mb-2 flex items-center gap-3 text-sm text-destructive"><p>{search.error}</p><Button variant="secondary" onClick={search.refetch} title="Try the device search again">Retry</Button></div>}
            {!search.loading && !search.error && query && search.rows.length === 0 && (
              <p className="text-sm text-muted-foreground">No devices match.</p>
            )}
            {!search.loading && !search.error && !query && search.rows.length === 0 && <p className="text-sm text-muted-foreground">No devices are available for cleanup.</p>}
            <ul className="divide-y">
              {!search.error && search.rows.map((d) => {
                const ctx = d.app_context;
                const owner = ctx.status === 'missing'
                  ? null
                  : (ctx.owner_email || ctx.owner_name || 'Unknown owner');
                return (
                  <li key={d.id} className="rounded-md px-2 py-2 hover:bg-muted/30 cursor-pointer" onClick={() => setDevice(d)}>
                      {/* Line 1: serial + status flags, id on the right */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <Button variant="link" className="min-w-0 truncate font-mono text-sm" title={`Choose ${d.name} for cleanup`} onClick={event => {
                            event.stopPropagation();
                            setDevice(d);
                          }}>{d.name}</Button>
                          <DecommissionedBadge decommissioned={d.decommissioned} />
                          {ctx.status === 'ambiguous' && (
                            <span className="rounded-sm border border-status-warning-border bg-status-warning px-1.5 py-0.5 text-[10px] font-semibold uppercase text-status-warning-foreground" title="More than one current app owner — pick carefully">multi-owner</span>
                          )}
                          {ctx.status === 'stale' && (
                            <span className="rounded-sm border border-status-neutral-border bg-status-neutral px-1.5 py-0.5 text-[10px] font-semibold uppercase text-status-neutral-foreground" title="App registration is stale">stale</span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">id {d.id}</span>
                      </div>
                      {/* Line 2: description · entity_path · app owner/location · filter */}
                      <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                        {d.description && <span className="font-medium text-gray-600">“{d.description}”</span>}
                        <span>{d.entity_path || '—'}</span>
                        {owner
                          ? <span>· owner: {owner}{ctx.location_name ? ` · ${ctx.location_name}` : ''}{ctx.hvac_name ? ` / ${ctx.hvac_name}` : ''}</span>
                          : <span className="text-muted-foreground">· no app registration</span>}
                        {ctx.filter_summary && <span>· filter: {ctx.filter_summary}</span>}
                      </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      {/* 2. Template + window + options */}
      {device && (
        <section className="mb-6 p-4 bg-white rounded-lg border space-y-4">
          <h2 className="font-semibold text-gray-700">2. What to do</h2>
          <div>
            <Label htmlFor="cleanup-template">Template</Label>
            <Select value={template} onValueChange={value => {
              const choice = TEMPLATES.find(item => item.key === value);
              if (choice) setTemplate(choice.key);
            }} items={TEMPLATES.map(item => ({ value: item.key, label: item.label }))}>
              <SelectTrigger id="cleanup-template" className="w-full" title="Which cleanup to run" aria-describedby="cleanup-template-help"><SelectValue /></SelectTrigger>
              <SelectContent>{TEMPLATES.map(item => <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>)}</SelectContent>
            </Select>
            <p id="cleanup-template-help" className="text-xs text-muted-foreground mt-1">{tpl.blurb}</p>
          </div>

          <div className="flex gap-4 items-end flex-wrap">
            <div>
              <Label htmlFor="cleanup-window">Window</Label>
              <p id="cleanup-window-help" className="text-xs text-muted-foreground">Which days the cleanup covers (UTC, end exclusive).</p>
              <Select value={mode} onValueChange={value => {
                const choice = tpl.modes.find(item => item === value);
                if (choice) setMode(choice);
              }} items={tpl.modes.map(item => ({ value: item, label: item }))}>
                <SelectTrigger id="cleanup-window" className="min-w-32" title="Which days the cleanup covers (UTC, end exclusive)" aria-describedby="cleanup-window-help"><SelectValue /></SelectTrigger>
                <SelectContent>{tpl.modes.map(item => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {needsCutoff && (
              <div>
                <Label htmlFor="cleanup-cutoff">Cutoff (local → UTC)</Label>
                <p id="cleanup-cutoff-help" className="text-xs text-muted-foreground">Start or end boundary for the chosen cleanup window.</p>
                <Input id="cleanup-cutoff" type="datetime-local" min={minLocal} max={maxLocal} value={cutoff} onChange={(e) => setCutoff(e.target.value)} aria-describedby="cleanup-cutoff-help" />
              </div>
            )}
            {needsRange && (
              <>
                <div>
                  <Label htmlFor="cleanup-start">Start</Label>
                  <p id="cleanup-start-help" className="text-xs text-muted-foreground">First day included in the cleanup window.</p>
                  <Input id="cleanup-start" type="datetime-local" min={minLocal} max={maxLocal} value={start} onChange={(e) => setStart(e.target.value)} aria-describedby="cleanup-start-help" />
                </div>
                <div>
                  <Label htmlFor="cleanup-end">End (exclusive)</Label>
                  <p id="cleanup-end-help" className="text-xs text-muted-foreground">First day excluded from the cleanup window.</p>
                  <Input id="cleanup-end" type="datetime-local" min={minLocal} max={maxLocal} value={end} onChange={(e) => setEnd(e.target.value)} aria-describedby="cleanup-end-help" />
                </div>
              </>
            )}
          </div>

          {(needsCutoff || needsRange) && range && (
            <p className="text-xs text-muted-foreground">
              {range.has_data
                ? `Device data spans ${new Date(range.min_data!).toLocaleString()} → ${new Date(range.max_data!).toLocaleString()}. Picker limited to this range (no future dates).`
                : 'No stored readings or daily calcs for this device.'}
            </p>
          )}

          <div className="flex flex-wrap gap-4 text-sm">
            {template === 'reprocess_only' && (
              <div>
                <div className="flex items-center gap-1">
                  <Label>
                    <Checkbox id="cleanup-keep-baseline" aria-label="Keep baseline" checked={keepBaseline} onCheckedChange={checked => setKeepBaseline(checked === true)} aria-describedby="cleanup-keep-baseline-help" title="Keep the filter baseline and its change date" />
                    <span>Keep baseline</span>
                  </Label>
                <InfoIcon text="The baseline is the filter's clean-state pressure-drop reference, recorded when the filter was installed — filter life is scored against it. Keep it to just replay the daily analytics; unchecking discards it and re-derives the reference from the readings. It applies to whichever of the device's analyses owns the baseline — the preview names it. The reprocess rebuilds the device's own enabled analyses; there is no analysis to pick." />
                </div>
                <p id="cleanup-keep-baseline-help" className="text-xs text-muted-foreground">Preserve the installed filter reference during reprocessing.</p>
              </div>
            )}
            {template === 'delete_recent_corrupted_data' && (
              <>
                <div>
                  <div className="flex items-center gap-1">
                    <Checkbox id="cleanup-reset-baseline" aria-label="Reset baseline" checked={resetBaseline} onCheckedChange={checked => setResetBaseline(checked === true)} aria-describedby="cleanup-reset-baseline-help" title="Re-measure the filter baseline after deleting corrupted data" />
                    <Label htmlFor="cleanup-reset-baseline">Reset baseline</Label>
                  <InfoIcon text="Also discards the filter's clean-state baseline — the install-time pressure-drop reference filter life is scored against — so it re-derives on the next run. Only needed when the corrupted window is what poisoned the baseline; otherwise just the window's readings are deleted and the baseline stands." />
                  </div>
                  <p id="cleanup-reset-baseline-help" className="text-xs text-muted-foreground">Discard and re-measure the installed filter reference.</p>
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <Checkbox id="cleanup-rebuild" aria-label="Rebuild after delete" checked={rebuild} onCheckedChange={checked => setRebuild(checked === true)} aria-describedby="cleanup-rebuild-help" title="Rebuild derived analytics after deleting readings" />
                    <Label htmlFor="cleanup-rebuild">Rebuild after delete</Label>
                  <InfoIcon text="Re-runs daily processing right after the delete to rebuild the derived analytics (daily calcs, runtimes, filter metrics) from the remaining readings. Leave it unchecked and the deleted window stays a gap until the next nightly run fills it." />
                  </div>
                  <p id="cleanup-rebuild-help" className="text-xs text-muted-foreground">Recreate daily analytics from the remaining readings.</p>
                </div>
              </>
            )}
            {template === 'return_to_inventory' && (
              <>
                <div>
                  <div className="flex items-center gap-1">
                    <Checkbox id="cleanup-clear-notes" aria-label="Clear notes" checked={clearNotes} onCheckedChange={checked => setClearNotes(checked === true)} aria-describedby="cleanup-clear-notes-help" title="Delete operator notes when returning this device to inventory" />
                    <Label htmlFor="cleanup-clear-notes">Clear notes</Label>
                  <InfoIcon text="Operator notes are the device's running record — QC findings, hardware observations, repairs. They describe the physical unit rather than its owner, so returning a device to inventory keeps them for the next deployment. Check this only when the notes themselves shouldn't follow the device." />
                  </div>
                  <p id="cleanup-clear-notes-help" className="text-xs text-muted-foreground">Delete operator notes as part of this cleanup.</p>
                </div>
                <span className="flex items-center gap-1 text-sm text-gray-600">
                  {RETURN_TO_INVENTORY_CLEARS} {RETURN_TO_INVENTORY_KEEPS}
                  <InfoIcon text={`${RETURN_TO_INVENTORY_LEAD} The device record is kept (same id, serial, IoT identity and calibration), and its IoT thing and certificate are never touched, so the reshipped Clip reconnects with no re-registration delay. Its data is deleted and its owner and QC fields (description, location, filter info) are reset. The next owner sets it up normally.`} />
                </span>
              </>
            )}
          </div>

          <ActionRow reason={scopeRefusal(template)} reasonId="cleanup-preview-refusal">
            <Button variant="primary" disabled={busy || !!scopeRefusal(template)} onClick={runPreview}
              aria-describedby={scopeRefusal(template) ? 'cleanup-preview-refusal' : undefined}
              title={scopeRefusal(template) ?? 'Count what this job would delete and rebuild. Nothing changes until you run it.'}>Preview blast radius</Button>
          </ActionRow>
        </section>
      )}

      {/* 3. Preview + confirm + execute */}
      {preview && (
        <section className="mb-6 p-4 bg-white rounded-lg border">
          <h2 className="font-semibold text-gray-700 mb-2">3. Preview</h2>
          {preview.reprocess && <ReprocessSummaryBox summary={preview.reprocess} />}
          <table className="w-full text-sm mb-3">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1">Component</th><th>Rows</th><th>Action</th></tr></thead>
            <tbody>
              {Object.entries(preview.counts).map(([k, v]) => {
                const destructive = preview.destructive_components.includes(k);
                return (
                  <tr key={k} className="border-t">
                    <td className="py-1 font-mono">{k}{COMPONENT_DESCRIPTIONS[k] && <InfoIcon text={COMPONENT_DESCRIPTIONS[k]} />}</td>
                    <td className={destructive && v > 0 ? 'text-status-error-foreground font-semibold' : ''}>{v}</td>
                    <td className={destructive ? 'text-status-error-foreground' : 'text-muted-foreground'}>{destructive ? 'DELETE' : 'preserve'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {preview.lifecycle && preview.lifecycle.length > 0 && <LifecycleTable rows={preview.lifecycle} />}
          {preview.deletes && <TableList testId="preview-deletes" title="Deleted" tables={preview.deletes} tone="red" />}
          {preview.keeps && <TableList testId="preview-keeps" title="Kept" tables={preview.keeps} tone="gray" />}
          {!preview.deletes && !preview.keeps && (() => {
            // Older API without the full table lists: destructive components with
            // no row-count (e.g. FK-cascade children that return_to_inventory
            // wipes but the preview doesn't separately count). Surface them so the
            // blast radius isn't understated by the counts table.
            const uncounted = preview.destructive_components.filter((c) => !(c in preview.counts));
            return uncounted.length > 0 ? (
              <div className="mb-3 rounded-sm border border-status-error-border bg-red-50 p-3">
                <p className="text-xs font-semibold text-red-700 mb-2">Also deleted (cascade — not separately counted)</p>
                <ul className="flex flex-wrap gap-x-3 gap-y-1">
                  {uncounted.map((c) => (
                    <li key={c} className="text-xs font-mono text-red-700">
                      {c}{COMPONENT_DESCRIPTIONS[c] && <InfoIcon text={COMPONENT_DESCRIPTIONS[c]} />}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null;
          })()}
          {preview.retained && preview.retained.length > 0 && (
            <div className="mb-3 rounded-sm border border-border bg-gray-50 p-3">
              <p className="text-xs font-semibold text-gray-600 mb-2">Retained — NOT deleted by this job</p>
              <ul className="space-y-2">
                {preview.retained.map((r) => (
                  <li key={r.component} className="text-xs">
                    <span className="font-mono text-gray-700">{r.component}</span>
                    <span className="ml-2 rounded-sm border border-status-neutral-border bg-status-neutral px-1.5 py-0.5 text-[10px] uppercase text-status-neutral-foreground">retained</span>
                    <p className="text-muted-foreground mt-0.5">{r.note}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-muted-foreground mb-3">Preview expires {new Date(preview.expires_at).toLocaleString()}{expired && ' — EXPIRED, re-preview'}.</p>

          {(() => {
            const refusal = scopeRefusal(preview.template);
            if (refusal) return <p className="text-sm text-muted-foreground">{refusal}</p>;
            const extra = TEMPLATE_EXTRA_PERM[preview.template];
            const missing = !canPurge ? 'purge_device_data' : (extra && !hasPermission(extra) ? extra : null);
            return missing ? (
              <p className="text-sm text-muted-foreground">You can preview but not execute (missing <code>{missing}</code>).</p>
            ) : (
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label htmlFor="cleanup-confirm-serial">Type the serial to confirm: <span className="font-mono">{preview.confirmation_required.typed_device_serial}</span></Label>
                  <p id="cleanup-confirm-help" className="text-xs text-muted-foreground">Type the exact serial shown above to enable this previewed cleanup.</p>
                  <Input id="cleanup-confirm-serial" className="font-mono" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="device serial" title="Type the device serial exactly to enable Run cleanup" aria-describedby="cleanup-confirm-help" />
                </div>
                {/* basis-96 (24rem) gives the row its width beside the serial
                    field, as ActionRow needs: room for the reason beside the button. */}
                <ActionRow
                  align="end"
                  className="basis-96"
                  reason={!confirmOk && (expired ? 'The preview expired. Preview again.' : 'Type the exact device serial to enable Run cleanup.')}
                  reasonId="cleanup-run-disabled"
                >
                  <Button variant="danger" disabled={!confirmOk || busy} onClick={runExecute}
                    aria-describedby={!confirmOk ? 'cleanup-run-disabled' : undefined}
                    title={confirmOk ? 'Run this previewed cleanup now' : (expired ? 'The preview expired — preview again' : 'Type the device serial to enable')}>Run cleanup</Button>
                </ActionRow>
              </div>
            );
          })()}
        </section>
      )}

      {/* 4. Job status */}
      {job && (
        <section className="mb-6 p-4 bg-white rounded-lg border">
          <h2 className="font-semibold text-gray-700 mb-2 break-all">4. Job {job.id}</h2>
          <p className="text-sm mb-2">State: <span className={`font-semibold ${job.state === 'completed' ? 'text-green-700' : job.state === 'failed' ? 'text-status-error-foreground' : 'text-amber-700'}`}>{job.state}</span>
            {!TERMINAL.has(job.state) && <span className="text-muted-foreground"> · polling…</span>}</p>
          {job.error && <p className="text-sm text-status-error-foreground mb-2">{job.error}</p>}
          {Object.keys(job.execution_counts).length > 0 && (
            <pre className="text-xs border border-border bg-muted rounded-sm p-2 mb-2 overflow-x-auto">{JSON.stringify(job.execution_counts, null, 2)}</pre>
          )}
          <ul className="text-sm">
            {job.steps.map((s) => (
              <li key={s.step_key} className="flex justify-between border-t py-1">
                <span className="font-mono">{s.step_key}</span>
                <span className={s.state === 'completed' ? 'text-green-700' : s.state === 'failed' ? 'text-status-error-foreground' : 'text-muted-foreground'}>{s.state}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default DataCleanupConsolePage;
